import { useState } from "react";
import FormFields from "./FormFields";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function FormSections({ formDetails, setFormDetails }) {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddSection = () => {
    setFormDetails((prev) => {
      const newSectionId = crypto.randomUUID();

      return {
        ...prev,
        sectionDetail: [
          ...prev.sectionDetail,
          {
            tempId: newSectionId,
            title: "Untitled Section",
            description: "section description...",
            relatedField: [
              {
                tempId: crypto.randomUUID(),
                label: { text: "Untitled Field", image: null },
                type: "input",
                placeholder: "New Field",
                options: [],
              },
            ],
          },
        ],
      };
    });
  };

  const addField = (sectionId) => {
    setFormDetails((prev) => ({
      ...prev,
      sectionDetail: prev.sectionDetail.map((section) =>
        (section._id || section.tempId) === sectionId
          ? {
              ...section,
              relatedField: [
                ...section.relatedField,
                {
                  _id: null,
                  tempId: crypto.randomUUID(),
                  label: { text: "Untitled Field", image: null },
                  type: "input",
                  placeholder: "New Field",
                  options: [],
                },
              ],
            }
          : section
      ),
    }));
  };

  const deleteField = (sectionId, fieldId) => {
    setFormDetails((prev) => ({
      ...prev,
      sectionDetail: prev.sectionDetail.map((section) =>
        (section._id || section.tempId) === sectionId
          ? {
              ...section,
              relatedField: section.relatedField.filter((field) => (field._id || field.tempId) !== fieldId),
            }
          : section
      ),
    }));
  };

  const updateField = (sectionId, fieldId, updatedField) => {
    setFormDetails((prev) => ({
      ...prev,
      sectionDetail: prev.sectionDetail.map((section) =>
        (section._id || section.tempId) === sectionId
          ? {
              ...section,
              relatedField: section.relatedField.map((field) => ((field._id || field.tempId) === fieldId ? updatedField : field)),
            }
          : section
      ),
    }));
  };

  return (
    <>
      {formDetails.sectionDetail.map((sections, i) => (
        <div key={sections._id || sections.tempId}>
          <div className="group max-w-[900px] mx-auto mt-[80px] bg-white rounded-xl shadow-md border border-t-[7px] border-t-primary gap-3 relative">
            <div className="p-6 border-b border-gray-100">
              <input
                type="text"
                value={sections.title}
                onChange={(e) =>
                  setFormDetails((prev) => ({
                    ...prev,
                    sectionDetail: prev.sectionDetail.map((s) =>
                      (s._id || s.tempId) === (sections._id || sections.tempId) ? { ...s, title: e.target.value } : s
                    ),
                  }))
                }
                placeholder="Section title"
                className="w-full text-3xl font-bold text-gray-800 outline-none border-b border-transparent focus:border-primary"
              />
              <textarea
                value={sections.description}
                onChange={(e) =>
                  setFormDetails((prev) => ({
                    ...prev,
                    sectionDetail: prev.sectionDetail.map((s) =>
                      (s._id || s.tempId) === (sections._id || sections.tempId) ? { ...s, description: e.target.value } : s
                    ),
                  }))
                }
                placeholder="Section description"
                className="mt-2 w-full text-gray-600 text-sm resize-none outline-none border-b border-transparent focus:border-primary"
              />
            </div>
            <span className="absolute left-1/2 -top-3 -translate-x-1/2 bg-white px-3 text-sm font-medium text-gray-700 rounded-md border border-primary">
              {`Section ${i + 1}`}
            </span>
            {i === formDetails.sectionDetail.length - 1 && (
              <button
                type="button"
                onClick={handleAddSection}
                className="absolute 
               -right-5 top-1/2 transform -translate-y-1/2 bg-green-600 text-white p-3 rounded-full shadow-lg hover:bg-green-500/90 transition
               opacity-0 group-hover:opacity-100 group/btn"
              >
                <Plus className="w-5 h-5" />
                <span
                  className="absolute left-full ml-2 top-1/2 -translate-y-1/2 
                 bg-green-500 text-white text-xs px-2 py-1 rounded-md 
                 opacity-0 group-hover/btn:opacity-100 
                 transition pointer-events-none whitespace-nowrap"
                >
                  Add Section
                </span>
              </button>
            )}

            {
              <button
                type="button"
                onClick={() => toggleSection(sections._id || sections.tempId)}
                className={`absolute left-1/2 bottom-0 translate-x-[-50%] translate-y-1/2
bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition group/btn
                ${openSections[sections._id || sections.tempId] ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              >
                {openSections[sections._id || sections.tempId] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                <span
                  className={`absolute ${
                    openSections[sections._id || sections.tempId] ? "bottom-full mb-2" : "top-full mt-2"
                  } left-1/2 -translate-x-1/2 
  bg-primary text-white text-xs px-2 py-1 rounded-md 
  opacity-0 group-hover/btn:opacity-100 
  transition pointer-events-none whitespace-nowrap`}
                >
                  {openSections[sections._id || sections.tempId] ? "Hide Fields" : "See Fields"}
                </span>
              </button>
            }
          </div>

          <AnimatePresence>
            {openSections[sections._id || sections.tempId] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
                className="max-w-[853px] mx-auto mt-8 space-y-6"
              >
                {sections.relatedField.map((field, index) => (
                  <FormFields
                    key={field._id || field.tempId}
                    field={field}
                    onAdd={index === sections.relatedField.length - 1 ? () => addField(sections._id || sections.tempId) : null}
                    onUpdate={(fieldId, updatedField) => updateField(sections._id || sections.tempId, fieldId, updatedField)}
                    onDelete={(fieldId) => deleteField(sections._id || sections.tempId, fieldId)}
                    index={index}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </>
  );
}
